/**
 * View ▾ — the dock's display options in one popover (spec §B2): the 3D
 * envelope / bands, Keep on map, point-cloud colour, Clip to interval and
 * Jump to station. They used to be five checkboxes and a radiogroup wrapping
 * across the header, which is a large part of why the header ate the dock.
 *
 * A Radix POPOVER, not a DropdownMenu: a menu item closes the menu when
 * chosen, and a reader flipping two switches in a row would have to reopen it
 * between them. The content is portalled, so it carries `sv-palette` to keep
 * any `var(--sv-*)` inside (the envelope key, the band colour bar) resolving
 * to the dock's theme.
 *
 * The trigger shows a dot while any 3D overlay is on (`active`), so state that
 * moved into the menu is still visible from outside it.
 */
import type { ReactNode } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import { DOCK_CONTROL, DOCK_TEXT } from './dockStyles';
import { type DockGate, DockSegmented, type DockSegmentedOption } from './DockToolbar';

export interface DockViewMenuProps {
  /** The trigger's `data-testid` (`clearance-view`, `sv-view`). */
  testId?: string;
  /** The popover content's `data-testid`. Defaults to `${testId}-menu`. */
  contentTestId?: string;
  /** Any 3D overlay on — draws the dot on the trigger. */
  active?: boolean;
  /** Defaults to the shared `dock.view` copy. */
  label?: string;
  /** Controlled open state, for a dock that opens it from a hotkey. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `DockMenuSection`s. */
  children: ReactNode;
}

export const DockViewMenu = ({
  testId,
  contentTestId,
  active = false,
  label,
  open,
  onOpenChange,
  children,
}: DockViewMenuProps) => {
  const { t } = useTranslation('project');
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid={testId}
          data-active={active ? 'true' : undefined}
          title={t('dock.viewHelp')}
          className={DOCK_CONTROL.button}
        >
          {label ?? t('dock.view')}
          {active && (
            <>
              <span aria-hidden className='size-1.5 rounded-full bg-sky-500 dark:bg-sky-400' />
              <span className='sr-only'>{t('dock.viewActive')}</span>
            </>
          )}
          <ChevronDown className={DOCK_CONTROL.iconSm} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        collisionPadding={8}
        data-testid={contentTestId ?? (testId ? `${testId}-menu` : undefined)}
        className='sv-palette flex max-h-[min(70vh,32rem)] w-72 flex-col overflow-y-auto border-slate-200 bg-white p-1 text-xs text-slate-700 shadow-lg dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200'
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export interface DockMenuSectionProps {
  /** "3D", "Point cloud colour", "Walk". */
  title: string;
  children: ReactNode;
  testId?: string;
}

/** A titled group inside `DockViewMenu`; sections are divided by a rule. */
export const DockMenuSection = ({ title, children, testId }: DockMenuSectionProps) => (
  <section
    data-testid={testId}
    aria-label={title}
    className='flex flex-col gap-0.5 py-1 not-first:mt-1 not-first:border-t not-first:border-slate-200 not-first:pt-2 dark:not-first:border-stone-700'
  >
    <h3 className={cn(DOCK_TEXT.metaMuted, 'px-2 pb-0.5 font-medium')}>{title}</h3>
    {children}
  </section>
);

export interface DockMenuSwitchProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /**
   * The paywall. Locked, the switch renders OFF (whatever `checked` says), is
   * `aria-disabled` + `data-locked`, shows an `UpgradeBadge`, and a click
   * opens the upgrade dialog — never the HTML `disabled` attribute, which
   * would swallow that click (CLAUDE.md).
   */
  gate?: DockGate;
  /**
   * A data reason the switch cannot act ("walk to a station first"). Inert,
   * `aria-disabled`, the reason shown under the label. Ignored while locked:
   * the paywall is the reason that has somewhere to go.
   */
  disabledReason?: string | null;
  /** One line under the label saying what the switch does. Wraps. */
  description?: string;
  /** On the switch itself — keeps `clearance-envelope`, `sv-bands`… working. */
  testId?: string;
  /** Under the row while it is on — the envelope key, the band colour bar. */
  children?: ReactNode;
}

/**
 * One switch row: the label, an optional description or reason, the switch at
 * the right. The whole row is the label, so the hit target is the row.
 */
export const DockMenuSwitch = ({
  label,
  checked,
  onCheckedChange,
  gate,
  disabledReason,
  description,
  testId,
  children,
}: DockMenuSwitchProps) => {
  const locked = gate ? !gate.allowed : false;
  const reason = locked ? null : (disabledReason ?? null);
  const inert = locked || !!reason;
  const shown = !locked && checked;
  const note = reason ?? description;
  return (
    <div className='flex flex-col'>
      <label
        className={cn(
          'flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-stone-800',
          reason && 'cursor-not-allowed',
        )}
        title={reason ?? description}
      >
        <span className={cn('flex min-w-0 flex-1 flex-col', inert && DOCK_CONTROL.inert)}>
          <span className='flex items-center gap-1.5'>
            <span className='text-slate-800 dark:text-stone-100'>{label}</span>
            {locked && gate && <UpgradeBadge gate={gate.gate} />}
          </span>
          {note && <span className={DOCK_TEXT.metaMuted}>{note}</span>}
        </span>
        <Switch
          data-testid={testId}
          checked={shown}
          aria-disabled={inert || undefined}
          data-locked={locked ? 'true' : undefined}
          onCheckedChange={(c) => {
            if (locked) {
              gate?.promptUpgrade();
              return;
            }
            if (reason) return;
            onCheckedChange(c);
          }}
          className={cn('shrink-0 data-[state=checked]:bg-sky-600 dark:data-[state=checked]:bg-sky-500', inert && DOCK_CONTROL.inert)}
        />
      </label>
      {shown && children != null && <div className='px-2 pt-0.5 pb-1.5'>{children}</div>}
    </div>
  );
};

export interface DockMenuRadioGroupProps<T extends string> {
  /** The group's accessible name (usually the section title). */
  ariaLabel: string;
  options: DockSegmentedOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  gate?: DockGate;
  testId?: string;
}

/**
 * An exclusive choice inside the menu — "Point cloud colour: Scalar /
 * Highlight / Violations only". `DockSegmented` in its `list` variant, so the
 * keyboard (↑/↓ move the check, unavailable options skipped) and the gating
 * are the tab bar's, not a second implementation.
 */
export const DockMenuRadioGroup = <T extends string>(props: DockMenuRadioGroupProps<T>) => (
  <DockSegmented {...props} variant='list' />
);
